import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getGoalMainByEngagementFn,
  getAllGoalsFn,
  updateGoalFn,
  createGoalFn,
  deleteGoalFn,
  setProgramGoalDoneFn,
} from "~/lib/functions/goal";
import type {
  GoalByIdInput,
  SetProgramGoalDoneInput,
  CreateGoalInput,
  UpdateGoalInput,
} from "~/lib/schemas/goal";
import type { HomeMainGoal } from "~/lib/shared/coaching-home";

export const goalQueryKeys = {
  all: ["goals"] as const,
  byEngagement: (engagementId: string) =>
    [...goalQueryKeys.all, "engagement", engagementId] as const,
  mainByEngagement: (engagementId: string) =>
    [...goalQueryKeys.all, "main", "engagement", engagementId] as const,
};

export const goalMainByEngagementQueryOptions = (engagementId: string) => ({
  queryKey: goalQueryKeys.mainByEngagement(engagementId),
  queryFn: () => getGoalMainByEngagementFn({ data: { engagementId } }),
  enabled: !!engagementId,
});

export const allGoalsQueryOptions = (engagementId: string) => ({
  queryKey: goalQueryKeys.byEngagement(engagementId),
  queryFn: () => getAllGoalsFn({ data: { engagementId } }),
  enabled: !!engagementId,
});

export const useGoalMainByEngagement = (
  engagementId: string,
  /**
   * When the caller already has the goal (e.g. carried by the home batch), seed
   * it as initialData so the query resolves immediately instead of waterfalling.
   * Undefined = fetch normally; null = a known "no goal" with no extra fetch state.
   */
  prefetched?: HomeMainGoal | null,
) => {
  return useQuery({
    ...goalMainByEngagementQueryOptions(engagementId),
    ...(prefetched !== undefined ? { initialData: prefetched } : {}),
  });
};

export const useAllGoals = (engagementId: string) => {
  return useQuery(allGoalsQueryOptions(engagementId));
};

export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGoalInput) => createGoalFn({ data }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: goalQueryKeys.byEngagement(variables.engagementId),
      });
      queryClient.invalidateQueries({
        queryKey: goalQueryKeys.mainByEngagement(variables.engagementId),
      });
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    },
  });
};

export const useUpdateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateGoalInput) => updateGoalFn({ data }),
    onSuccess: (_result, variables) => {
      if (variables.engagementId) {
        queryClient.invalidateQueries({
          queryKey: goalQueryKeys.byEngagement(variables.engagementId),
        });
        queryClient.invalidateQueries({
          queryKey: goalQueryKeys.mainByEngagement(variables.engagementId),
        });
      }

      queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    },
  });
};

export const useDeleteGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GoalByIdInput) => deleteGoalFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    },
  });
};

/** Marks a program goal done or reopens it; both lists and the recap read the new status. */
export const useSetProgramGoalDone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetProgramGoalDoneInput) =>
      setProgramGoalDoneFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    },
  });
};
